import React, { useRef, useEffect, useState } from 'react';
import './MovingBanner.css';

const MovingBanner = ({ items, speed = 20, isImages = false }) => {
  const bannerRef = useRef(null);
  const contentRef = useRef(null);
  const [needsAnimation, setNeedsAnimation] = useState(true);
  const [isSingleItem, setIsSingleItem] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Check layout requirements
  useEffect(() => {
    const checkLayout = () => {
      if (bannerRef.current && contentRef.current) {
        const containerWidth = bannerRef.current.offsetWidth;
        const singleItem = items.length === 1;
        
        setIsSingleItem(singleItem);
        setNeedsAnimation(!singleItem && (contentRef.current.scrollWidth > containerWidth));
      }
    };

    checkLayout();
    window.addEventListener('resize', checkLayout);
    return () => window.removeEventListener('resize', checkLayout);
  }, [items]);

  // Event handlers
  const handleStart = (e) => {
    if (!needsAnimation) return;
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    setIsDragging(true);
    setStartX(clientX);
    setScrollLeft(bannerRef.current.scrollLeft);
    bannerRef.current.style.animationPlayState = 'paused';
  };

  const handleMove = (e) => {
    if (!isDragging || !needsAnimation) return;
    e.preventDefault();
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    bannerRef.current.scrollLeft = scrollLeft - (clientX - startX);
  };

  const handleEnd = () => {
    setIsDragging(false);
    if (needsAnimation) bannerRef.current.style.animationPlayState = 'running';
  };

  // Cleanup event listeners
  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;

    const events = [
      ['touchstart', handleStart],
      ['mousedown', handleStart],
      ['touchmove', handleMove],
      ['mousemove', handleMove],
      ['touchend', handleEnd],
      ['mouseup', handleEnd],
      ['mouseleave', handleEnd]
    ];

    events.forEach(([event, handler]) => banner.addEventListener(event, handler));
    return () => events.forEach(([event, handler]) => banner.removeEventListener(event, handler));
  }, [isDragging, startX, scrollLeft, needsAnimation]);

  return (
    <div className={`banner-container ${isDragging ? 'grabbing' : ''} ${isSingleItem ? 'single-item' : ''}`} ref={bannerRef}>
      <div className="banner-content" ref={contentRef}>
        {(needsAnimation && !isSingleItem) ? (
          // Duplicated items for seamless looping
          [...items, ...items].map((item, i) => (
            <BannerItem key={`dup-${i}`} item={item} isImages={isImages} />
          ))
        ) : (
          // Single item or non-animated display
          items.map((item, i) => (
            <BannerItem key={`item-${i}`} item={item} isImages={isImages} isSingle={isSingleItem} />
          ))
        )}
      </div>
      {needsAnimation && !isSingleItem && (
        <>
          <div className="banner-fade left" />
          <div className="banner-fade right" />
        </>
      )}
    </div>
  );
};

const BannerItem = ({ item, isImages, isSingle = false }) => (
  <div className={`banner-item ${isSingle ? 'full-width' : ''}`}>
    {isImages ? (
      <img src={item} alt="" className="banner-image" draggable="false" />
    ) : (
      item
    )}
  </div>
);

export default MovingBanner;